import type { User } from '@auth0/auth0-react';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { AuthContext } from '../contexts/AuthProvider';
import { useAuthTracking } from './useAuthWithTracking/internal/useAuthTracking';

/**
 * Hook that extends Auth0's authentication with tracking capabilities.
 * This is the only hook that should be used for authentication in the application.
 */
export const useAuthWithTracking = () => {
  const auth = useContext(AuthContext);
  if (auth === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  const { trackLogin, trackAuthInitiated, trackLogout, clearLoginTracking } = useAuthTracking();

  // Track authentication state changes
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      trackLogin(auth.user);
    } else {
      clearLoginTracking();
    }
  }, [auth.isAuthenticated, auth.user, trackLogin, clearLoginTracking]);

  const loginWithRedirect = useCallback(
    async (...args: Parameters<typeof auth.loginWithRedirect>) => {
      trackAuthInitiated();
      return auth.loginWithRedirect(...args);
    },
    [auth, trackAuthInitiated],
  );

  const logout = useCallback(
    (...args: Parameters<typeof auth.logout>) => {
      trackLogout();
      auth.logout(...args);
    },
    [auth, trackLogout],
  );

  return useMemo(
    () => ({
      ...auth,
      loginWithRedirect,
      logout,
    }),
    [auth, loginWithRedirect, logout],
  );
};
